import { useMemo, useState, type ComponentProps } from 'react';
import { teamFilter } from '../use-teams';
import {
  addWireDays,
  formatHours,
  parseHours,
  type Task,
  type TaskDependencyEdge,
  type TaskPriority,
  type Metafield,
  type TaskStatus,
  type TaskTrackerValues,
} from '@forecastify/shared';
import {
  Board,
  DataGrid,
  DataList,
  ListCardBody,
  ListPager,
  resolveCard,
  useListLayoutStore,
  type CardSpec,
  Gantt,
  ListToolbar,
  SKELETON_WIDTHS,
  Skeleton,
  skeletonWidth,
  useColumnChoice,
  type BoardColumn,
  type DataGridColumn,
  type DataGridRow,
  type GanttDependency,
  type GanttRow,
  type GanttUnit,
  type ListFilter,
  type PagerState,
  type SortState,
} from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { taskColumns, taskRows } from './task-columns';
import { NO_TRACKER_VALUES } from './tracker-filters';
import { initialsOf } from '../identity';
import { useAvatars } from '../use-avatar';
import { ListHeader, usePageHeader } from '../page-header';
import { listLabels } from '../delivery/clients-screen';
import { useCalendarLabels } from '../date-field';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ THE FOUR TASK VIEWS — one screen, one filter set, four bodies.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * C9. `TASK_VIEWS` is the mockup's own list, in its order: **Lista, Kanban, Idővonal, Rács.**
 *
 * ⚠️⚠️ **ONE TOOLBAR AND ONE FILTER SET ABOVE ALL FOUR — that is the point of the view switch.**
 * Four screens with four search boxes is four places for a filter to behave differently, and
 * switching view would silently reset what somebody had narrowed to. The mockup renders the same
 * `ListToolbar` above each of its four bodies for the same reason.
 *
 * ⚠️ **The four views disagree about what they can show, and that is honest rather than a bug:**
 *
 *  * **Lista** and **Rács** show every task;
 *  * **Kanban** groups by status, so a task is in exactly one column — nothing is dropped;
 *  * **Idővonal** can only draw a task that HAS BOTH DATES. An unscheduled task has no bar to draw,
 *    so the timeline says how many it left out rather than pretending the list is shorter.
 *
 * ⚠️ **Filtering is server-side already** (`task.service.ts` builds the WHERE clause), so the
 * toolbar's controls are lifted state the route turns into query parameters — not an in-memory
 * `.filter()` over a page. C7's client and project lists diverge from that deliberately because
 * they are bounded; a task list is not.
 */

export type TaskView = 'list' | 'board' | 'timeline' | 'grid';

/** The mockup's `TASK_VIEWS`, values and order unchanged. */
const VIEWS = [
  { value: 'list', icon: 'list-checks' },
  { value: 'board', icon: 'kanban' },
  { value: 'timeline', icon: 'chart-column' },
  { value: 'grid', icon: 'layout-grid' },
] as const;

/** ⚠️ One `t()` per branch with a literal key — a computed key is invisible to `i18n:check`. */
function viewLabel(t: Translate, view: TaskView): string {
  if (view === 'list') return t('tasks.view.list');
  if (view === 'board') return t('tasks.view.board');
  if (view === 'timeline') return t('tasks.view.timeline');
  return t('tasks.view.grid');
}

/** The three zooms, with the mockup's own `dayWidth` for each. */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ A ZOOM IS A UNIT AND A SCALE, WHERE IT USED TO BE A PIXEL WIDTH.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Item 14, second pass, 15 Sep — *"in the month view I don't want to see days, I want to see
 * columns that are wide FOR THE MONTH"*. Zoom was `dayWidth` alone: the same day-by-day ruler
 * squeezed until its numbers were a smear. The first pass relabelled that ruler, which is a caption
 * rather than a zoom; this one changes what a COLUMN IS, so the month setting has 200px month
 * columns and a bar sits a fraction of the way across one. `gantt.tsx` carries the geometry.
 */
const ZOOMS = {
  /* ⚠️ **Hét** draws DAY columns — which is what Jira's own *Weeks* does, for the reason `gantt.tsx`
     gives: a seven-day column would be a row of unlabelled boxes, a week having no name. The week
     still reads, as the stronger gridline every Monday. */
  week: { unitWidth: 30, unit: 'day' },
  /* ⚠️ One column per month, and one per quarter at the same width. Measured off Jira's own chart
     rather than picked: wide enough for a month name under a year, narrow enough that three years
     is a few screens rather than thirty. */
  month: { unitWidth: 200, unit: 'month' },
  quarter: { unitWidth: 200, unit: 'quarter' },
} as const satisfies Record<string, { unitWidth: number; unit: GanttUnit }>;
export type Zoom = keyof typeof ZOOMS;

export interface TaskFilters {
  search: string;
  projectId: string;
  statusId: string;
  priorityId: string;
  assigneeMemberId: string;
  /** R-60 · tasks whose assignee is in this team. */
  teamId: string;
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ ITEM 16 · THE COLUMNS THAT COULD BE SORTED AND NOT FILTERED.         │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * *"We need filters for ALL columns, that includes deadline, estimate, metafields."* Four of the
   * list's columns had a chip and the rest had a sort arrow, so *"show me everything due this
   * month"* was answered by ordering by date and reading until the dates stopped mattering.
   *
   * ⚠️ **`from..to`, one string** — `list-toolbar.tsx` argues the encoding. Empty means no filter,
   * and either end may be blank: `8..` is *at least eight hours*.
   */
  dueRange: string;
  estimateRange: string;
  /**
   * The workspace's own fields, keyed by metafield key.
   *
   * ⚠️ **A map rather than a list**, because the toolbar identifies a filter by key and a list
   * would need a find-and-splice on every keystroke. The route turns each entry into `mf.<key>=`,
   * which the server has understood since item 35 — the predicate existed and nothing drew it.
   */
  metafields: Readonly<Record<string, string>>;
  /**
   * ⚠️ **J-1 · what the tracker files a synced task under** — a component or fix version by NAME,
   * an epic by its remote id. `''` is no filter, like every other field here.
   */
  component: string;
  fixVersion: string;
  epic: string;
}

export function TasksScreen({
  page,
  loading = false,
  tasks,
  statuses,
  priorities,
  projects,
  members,
  teams = [],
  filters,
  onFiltersChange,
  sort = [],
  onSortChange,
  canCreate,
  canEdit,
  onCreate,
  embedded,
  scopeProjectId,
  onOpen,
  onMoveStatus,
  onBulkEdit,
  onBulkDelete,
  metafields,
  onReschedule,
  onGridEdit,
  onExport,
  onImport,
  initialView,
  onViewChange,
  mine = false,
  dependencies,
  serverError,
  trackerValues = NO_TRACKER_VALUES,
  boardColumns,
}: {
  /**
   * ⚠️ **The pages of the list, the grid and the timeline** — one server page each, `?offset=`.
   * The board does not take this one: each of its columns pages on its own (`boardColumns`).
   */
  readonly page?: ComponentProps<typeof DataList>['page'];
  /**
   * ⚠️ 31 Oct 2026 · **the board's columns, each paged on the server by its status.** Keyed by
   * status id: the column's filtered total for its header, and its pager when it has more than one
   * page. Absent — every embedded copy and every test — the board counts the cards it was handed.
   */
  readonly boardColumns?: Readonly<Record<string, BoardColumnPage>>;
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly tasks: readonly Task[];
  /**
   * J-1 · the components, fix versions and epics the synced tasks in reach carry. Each filter is
   * drawn only when its list has something in it, so a workspace with no tracker sees none of the
   * three. Optional, so every test and the setup preview keep working without it.
   */
  readonly trackerValues?: TaskTrackerValues;
  readonly statuses: readonly TaskStatus[];
  readonly priorities: readonly TaskPriority[];
  readonly projects: readonly { id: string; name: string }[];
  readonly members: readonly { id: string; name: string }[];
  /** R-60 · the assignee's team, as a filter. Absent where nothing passes the list. */
  readonly teams?: readonly { readonly id: string; readonly name: string }[];
  readonly filters: TaskFilters;
  readonly onFiltersChange: (filters: TaskFilters) => void;
  /**
   * ⚠️ **The LIST's sort, and only the list's.** A board distributes tasks into columns and a
   * timeline into dates; neither has an order a header could set, so the route sends this to the
   * server only while the list is showing.
   *
   * ⚠️ Optional, so every embedded copy of this screen and every test keeps working without it.
   */
  readonly sort?: readonly SortState[];
  readonly onSortChange?: (next: readonly SortState[]) => void;
  readonly canCreate: boolean;
  readonly canEdit: boolean;
  readonly onCreate: () => void;
  readonly onOpen: (task: Task) => void;
  /** Kanban. The screen does NOT write the move — the route does, and offers its undo. */
  readonly onMoveStatus: (task: Task, statusId: string) => void;
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️ ITEM 72 · what a SELECTION can be told to do.                      │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️ **Status, priority, assignee and delete — and not the rest of a task.** A bulk title or
   * description writes one sentence onto forty rows, which is never what somebody means; an
   * estimate belongs to one task's scope. These four are genuinely properties of a selection.
   *
   * ⚠️ Optional together, so every embedded copy of this screen keeps working without them.
   */
  readonly onBulkEdit?: (
    ids: readonly string[],
    patch: { statusId?: string; priorityId?: string; assigneeMemberId?: string | null },
  ) => void;
  readonly onBulkDelete?: (ids: readonly string[]) => void;
  /** ⚠️ Item 27 · the workspace's `task` fields. Those with `inList` become columns. */
  readonly metafields?: readonly Metafield[];
  /** Timeline. Both ends always given. */
  readonly onReschedule: (task: Task, startsOn: string, dueOn: string) => void;
  /** Rács. One cell at a time, which is what `DataGrid` reports. */
  readonly onGridEdit: (task: Task, column: string, value: string) => void;
  /* ⚠️ T-10's shared paths — this screen holds neither a CSV writer nor a parser. */
  readonly onExport: () => void;
  readonly onImport: () => void;
  /**
   * Which view to open on.
   *
   * The projects list's row menu offers *Idővonal* as well as *Feladatok szűrése erre*, so the
   * address it navigates to has to be able to say which of the four it meant. Only the FIRST
   * value is honoured — the switch stays the reader's after that, so a re-render cannot drag them
   * back to the view the link named.
   */
  readonly initialView?: TaskView;
  /**
   * ⚠️⚠️ **THE ROUTE HAS TO KNOW WHICH VIEW IS SHOWING, BECAUSE THE VIEWS PAGE DIFFERENTLY.** The
   * list, the grid and the timeline ask for one page of the filtered set; the board asks for one
   * page per status column (31 Oct 2026 — it used to read every task, and 29,591 on UAT is past
   * any ceiling). The route cannot build either request without being told which.
   */
  readonly onViewChange?: (view: TaskView) => void;
  /**
   * ⚠️ *Feladataim*: the assignee is already answered, so its chip is not offered. The FILTER is
   * still applied — the route pins it — which is why this only removes a control.
   */
  readonly mine?: boolean;
  /**
   * The timeline's dependency edges — see `taskDependencyEdgeSchema`.
   *
   * ⚠️ **Optional, so every test that renders this screen bare keeps working** and the chart simply
   * draws no arrows, which is what it did before this existed.
   */
  readonly dependencies?: readonly TaskDependencyEdge[];
  readonly serverError: string | null;
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ EMBEDDED IN ANOTHER SCREEN — item 30.                            │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * *"Project tasks gain the kanban / grid / timeline / list switch the global screen has."* The
   * project's own tab said as much when it was written: *"when C9 lands, the switcher goes above
   * this and the columns stay"*. C9 landed and it never did — so a project's tasks were a bare
   * list while the same tasks one screen over had four views.
   *
   * ⚠️⚠️ **THIS RATHER THAN A FIFTH COPY OF THE FOUR VIEWS.** The board, the timeline and the grid
   * are three hundred lines each and every one of them would have drifted from its twin — which is
   * the exact failure `task-columns.ts` was extracted to prevent one level down.
   *
   * ⚠️⚠️ **AN EMBEDDED COPY CLAIMS NEITHER THE CHROME NOR THE MARKER.** `usePageHeader` writes into
   * the shell's header, which the PROJECT screen already owns — an embedded copy would replace the
   * project's name with *Feladatok* — and `data-screen` is what the route-render gate reads to
   * prove an address drew its own screen. `person-screen.tsx` records both traps in full.
   */
  readonly embedded?: boolean;
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE PROJECT AS *SCOPE*, WHICH IS NOT THE SAME AS A FILTER.       │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * The project tab used to pin its project into `filters.projectId`, and the screen believed it.
   * Three things went wrong, and all three were the same mistake:
   *
   * 1. **Every empty board column claimed a filter had hidden its cards** — *"A szűrő minden
   *    feladatot elrejtett ebből az oszlopból"* — with no filter set. A column of a project with
   *    nothing in *Következik* is empty, not filtered.
   * 2. **The counts read as a filtered subset of a bigger set.** They were never that; the list is
   *    the project's, and saying otherwise invites somebody to go looking for the rest.
   * 3. **Clearing filters would have offered to widen the list past the project**, which is not
   *    something this screen is allowed to do.
   *
   * So the project arrives separately: it narrows the data exactly as before, and it is invisible
   * to `isFiltered`, to the chips, and to the filter menu. A filter is something a reader chose and
   * can undo; the address is not.
   */
  readonly scopeProjectId?: string;
}) {
  const t = useT();
  /* ⚠️ *Új feladat* in the header, where the mockup draws it — and NOT when embedded. */
  /**
   * ⚠️⚠️ **EMBEDDED MEANS *DO NOT CLAIM*, AND RETURNING `null` FROM THE BUILDER IS NOT THAT.** A
   * `null` builder still writes into the slot, so an embedded copy wiped the header its parent had
   * just set — the project's name, client, status pill and kebab all vanished the moment somebody
   * opened the Feladatok tab, and stayed gone on the way back. The third argument makes the hook
   * inert instead; `page-header.tsx` carries the whole sequence.
   */
  /**
   * ⚠️⚠️ **ÚJ FELADAT MOVED INTO THE TOOLBAR — item 25, second pass, 15 Sep.** It was a purple
   * CTA in the header here and an outline button in the toolbar on contracts, for the same act.
   * The toolbar is where it goes on every list now: one line with the search, not purple.
   *
   * ⚠️ **Which also deletes a special case.** The embedded copy already had to put the create in
   * its toolbar, because an embedded screen does not claim the header — so the button lived in two
   * places with a condition deciding which. One place, no condition.
   */
  usePageHeader(() => <ListHeader title={t('title.tasks')} />, [], embedded !== true);

  const [view, setView] = useState<TaskView>(initialView ?? 'list');
  const [zoom, setZoom] = useState<Zoom>('week');
  /* ⚠️ A token, not a date — the Gantt re-centres when this changes, so pressing *Ma* twice
   * scrolls twice. Keying on the date would make the second press do nothing. */
  const [centre, setCentre] = useState(0);

  /**
   * ⚠️ **The scope is deliberately absent from this list** — see `scopeProjectId`. *Filtered* means
   * *a reader narrowed it and can widen it again*, and that is what the chips and every *clear*
   * affordance are about.
   *
   * ⚠️⚠️ **AND THE BOARD NO LONGER READS IT — item 11, 15 Sep.** An empty COLUMN used to change its
   * sentence on this flag, which put a claim about one column behind a fact about the whole screen:
   * on the global board, one filter anywhere made every empty column announce that a filter had
   * emptied it. The only place that can honestly tell the two apart is the LIST's own empty state,
   * which is where this now goes.
   */
  const isFiltered =
    filters.search !== '' ||
    filters.projectId !== '' ||
    filters.statusId !== '' ||
    filters.priorityId !== '' ||
    filters.assigneeMemberId !== '' ||
    filters.dueRange !== '' ||
    filters.estimateRange !== '' ||
    filters.component !== '' ||
    filters.fixVersion !== '' ||
    filters.epic !== '' ||
    Object.values(filters.metafields).some((value) => value !== '');

  const patch = (part: Partial<TaskFilters>): void => {
    onFiltersChange({ ...filters, ...part });
  };

  /**
   * ⚠️⚠️ **ONE TOOLBAR, AND THE VIEW DECIDES WHO DRAWS IT — the mockup's own shape.**
   * `markup.html` 455 hands the LIST view's `DataList` the search, the filters, the saved views and
   * the view switch, and renders NO separate `ListToolbar` above it; 401, 442 and 449 render the
   * standalone toolbar for the three views that are not a `DataList`.
   *
   * ⚠️ Rendering both stacks two search boxes, which is what shipped and what was reported. The
   * props live in an object so the same set can go to either.
   */
  /**
   * ⚠️ **The chip reads the range back in words**, because `2026-09-01..` is an encoding rather
   * than a sentence. One `t()` per branch with a literal key — rule 7's shape.
   */
  /* ⚠️ The app's one translated calendar vocabulary — `date-field.tsx`. `ListToolbar` lives in
     `@forecastify/ui` and cannot reach `useT`, so the date bounds take it as a prop. */
  const calendar = useCalendarLabels();

  const rangeChip = (value: string, unit: 'date' | 'hours' | 'plain'): string => {
    const [from = '', to = ''] = value.split('..');
    /* ⚠️ Hours are formatted `H:MM` by the one helper every other hour in the product goes
       through; a date and a workspace's own number are shown as they were typed. */
    const show = (bound: string): string => (unit === 'hours' ? formatHours(Number(bound)) : bound);
    if (from !== '' && to !== '') return t('list.rangeBoth', { from: show(from), to: show(to) });
    if (from !== '') return t('list.rangeFromOnly', { from: show(from) });
    return t('list.rangeToOnly', { to: show(to) });
  };

  const rangeFilters: ListFilter[] = [
    {
      key: 'due',
      label: t('project.tasks.column.due'),
      value: filters.dueRange,
      kind: 'range',
      input: 'date',
      calendar,
      today: todayWire(),
      options: [],
      display: (value) => rangeChip(value, 'date'),
      onChange: (value) => {
        patch({ dueRange: value });
      },
    },
    {
      key: 'estimate',
      label: t('task.field.estimate'),
      value: filters.estimateRange,
      kind: 'range',
      input: 'number',
      options: [],
      display: (value) => rangeChip(value, 'hours'),
      onChange: (value) => {
        patch({ estimateRange: value });
      },
    },
  ];

  /**
   * ⚠️⚠️ **A CHOICE WHERE THE FIELD HAS OPTIONS, A RANGE WHERE IT IS A NUMBER OR A DATE, AND
   * NOTHING AT ALL FOR THE REST.** `select`, `multi` and `checkbox` are closed sets and belong in a
   * menu; `number` and `date` are continuous and belong in two bounds. A free-text field is left
   * out on purpose: the server matches it by EQUALITY (`metafield-filter.ts`), so a text box beside
   * it would promise a search and deliver *"type the value exactly"* — which reads as a broken
   * filter rather than as a narrow one. The list's own search box is where free text belongs.
   */
  const metafieldFilters: ListFilter[] = (metafields ?? [])
    .filter((field) => field.inList)
    .flatMap((field) => {
      const value = filters.metafields[field.key] ?? '';
      const set = (next: string): void => {
        patch({ metafields: { ...filters.metafields, [field.key]: next } });
      };

      if (field.type === 'select' || field.type === 'multi') {
        return [
          {
            key: `mf.${field.key}`,
            label: field.name,
            value,
            options: field.options.map((option) => ({ value: option, label: option })),
            onChange: set,
          },
        ];
      }
      if (field.type === 'checkbox') {
        return [
          {
            key: `mf.${field.key}`,
            label: field.name,
            value,
            options: [
              { value: 'true', label: t('common.yes') },
              { value: 'false', label: t('common.no') },
            ],
            onChange: set,
          },
        ];
      }
      if (field.type === 'number' || field.type === 'date') {
        return [
          {
            key: `mf.${field.key}`,
            label: field.name,
            value,
            kind: 'range' as const,
            input: field.type,
            ...(field.type === 'date' ? { calendar, today: todayWire() } : {}),
            options: [],
            display: (entry: string) => rangeChip(entry, field.type === 'date' ? 'date' : 'plain'),
            onChange: set,
          },
        ];
      }
      /**
       * ⚠️ **`text`, `textarea`, `url` and `user` are LEFT OUT, and that is the honest answer.**
       * The server matches a metafield by equality (`metafield-filter.ts`), so a text box here
       * would promise a search and deliver *"type the value exactly, including its case"* — which
       * reads as a filter that does not work rather than as one that is narrow. Free text is what
       * the search box beside these chips is for, and it already covers the title and the key.
       */
      return [];
    });

  /**
   * ⚠️ **J-1 · the tracker's three, each only when some task in reach carries a value.** The
   * options are what the synced tasks have rather than the tracker's catalogue — Loginet's runs to
   * hundreds, and the toolbar puts a search over any long list on its own.
   *
   * ⚠️ **A filter already set stays drawn even when its list empties**, so its chip can still be
   * removed — a filter that can only be seen and not cleared is the trap `project` above avoids.
   */
  const trackerFilter = (
    key: 'component' | 'fixVersion' | 'epic',
    label: string,
    options: readonly { value: string; label: string }[],
  ): ListFilter[] =>
    options.length === 0 && filters[key] === ''
      ? []
      : [
          {
            key,
            label,
            value: filters[key],
            onChange: (value: string) => {
              patch({ ...filters, [key]: value });
            },
            options: [...options],
          },
        ];
  const trackerFilters = [
    ...trackerFilter(
      'component',
      t('task.field.components'),
      trackerValues.components.map((name) => ({ value: name, label: name })),
    ),
    ...trackerFilter(
      'fixVersion',
      t('task.field.fixVersions'),
      trackerValues.fixVersions.map((name) => ({ value: name, label: name })),
    ),
    ...trackerFilter(
      'epic',
      t('task.field.epic'),
      trackerValues.epics.map((epic) => ({ value: epic.id, label: epic.name })),
    ),
  ];

  const toolbar: ComponentProps<typeof ListToolbar> = {
    search: filters.search,
    onSearchChange: (value) => {
      patch({ search: value });
    },
    filters: [
      {
        key: 'status',
        label: t('project.tasks.column.status'),
        value: filters.statusId,
        onChange: (value) => {
          patch({ statusId: value });
        },
        options: statuses.map((status) => ({ value: status.id, label: status.name })),
      },
      /**
       * ⚠️⚠️ **`task.field.project`, WHERE THIS SAID *Ügyfél* — it filtered by PROJECT and was
       * labelled *client*.** Two different things, and the chip repeated the wrong one back at
       * whoever set it.
       *
       * ⚠️⚠️ **AND IT IS OMITTED WHEN THERE ARE NO PROJECTS TO OFFER.** The project's own Feladatok
       * tab embeds this screen with `projects={[]}` and a `projectId` already applied, so the
       * toolbar drew a filter with an empty menu and a chip whose value matched none of its
       * options — falling back to printing the raw uuid. A filter that can only be removed is not
       * a filter, and inside a project it is a question already answered by the address.
       */
      /* ⚠️ And never inside a project — `scopeProjectId` is the address, not a choice. */
      ...(projects.length === 0 || scopeProjectId !== undefined
        ? []
        : [
            {
              key: 'project',
              label: t('task.field.project'),
              value: filters.projectId,
              onChange: (value: string) => {
                patch({ projectId: value });
              },
              options: projects.map((project) => ({ value: project.id, label: project.name })),
            },
          ]),
      {
        /* ⚠️ The mockup's `prFilter`, which C9's first draft left out — see the note on
         * `TaskFilter.priorityId`. Server-side, like every other filter here. */
        key: 'priority',
        label: t('project.tasks.column.priority'),
        value: filters.priorityId,
        onChange: (value) => {
          patch({ priorityId: value });
        },
        options: priorities.map((priority) => ({ value: priority.id, label: priority.name })),
      },
      ...(mine
        ? []
        : [
            {
              key: 'assignee',
              label: t('project.tasks.column.assignee'),
              value: filters.assigneeMemberId,
              onChange: (value: string) => {
                patch({ assigneeMemberId: value });
              },
              options: members.map((member) => ({ value: member.id, label: member.name })),
            },
            ...teamFilter(t, teams, filters.teamId, (value) => {
              patch({ teamId: value });
            }),
          ]),
      /**
       * ┌───────────────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ ITEM 16 · THE REST OF THE COLUMNS, WHICH HAD NO CHIP AT ALL.         │
       * └───────────────────────────────────────────────────────────────────────────┘
       *
       * *"We need filters for ALL columns, that includes deadline, estimate, metafields."* The
       * deadline's two bounds have been on the server since C27.2 and `debt.md` carried the missing
       * control by name; the estimate's are new; the metafields' have existed since item 35 and
       * were reachable only by typing `?mf.<key>=` into the address bar.
       *
       * ⚠️ **A range, not a menu of buckets.** *This week* and *next month* are somebody else's
       * idea of the question — `list-toolbar.tsx` argues the shape and the `from..to` encoding.
       */
      ...rangeFilters,
      ...trackerFilters,
      ...metafieldFilters,
    ],
    viewSwitch: {
      value: view,
      onChange: (value) => {
        setView(value as TaskView);
        onViewChange?.(value as TaskView);
      },
      items: VIEWS.map((entry) => ({
        value: entry.value,
        label: viewLabel(t, entry.value),
        icon: entry.icon,
      })),
    },
    actions: [
      /**
       * ⚠️⚠️ **THE CREATE LIVES HERE, ALWAYS — item 30, then item 25.** Item 30 put it in the
       * toolbar for the EMBEDDED copy only, because an embedded screen does not claim the header
       * and the tab would otherwise have four views and no way to add a task to any of them. Item
       * 25 removed the other half: a create belongs beside the search on every list, so the
       * condition went and the header keeps only the title.
       */
      ...(canCreate
        ? [
            {
              label: t('project.tasks.create'),
              icon: 'plus' as const,
              /* ⚠️ `primary` is what pins it to the RIGHT EDGE — it was written first in this
                 array and drew first, so the export menu, the zoom and the view switch all sat
                 to the right of the one button people open this screen to press. */
              primary: true,
              onSelect: onCreate,
            },
          ]
        : []),
      /* ⚠️ The mockup puts an Export arrow on both task views (`main.jsx` 3071 and 3075) and an
       * import beside it. Both go through the shared dialogs — see `logs-screen.tsx`. */
      {
        label: t('io.actions'),
        /* ⚠️ `as const` on each icon: an object literal widens `icon` to `string`, which is not
         * `IconName` — the same shape every other toolbar action in this file needs. */
        icon: 'database' as const,
        items: [
          { label: t('export.run'), icon: 'download' as const, onSelect: onExport },
          { label: t('import.run'), icon: 'upload' as const, onSelect: onImport },
        ],
      },
      ...(view === 'timeline'
        ? [
            {
              /* The zoom lives in the toolbar's actions, exactly as the mockup's
               * `timelineActions` does — it is a property of this view only. */
              label: zoomLabel(t, zoom),
              icon: 'calendar' as const,
              items: [
                {
                  label: t('tasks.zoom.week'),
                  onSelect: () => {
                    setZoom('week');
                  },
                },
                {
                  label: t('tasks.zoom.month'),
                  onSelect: () => {
                    setZoom('month');
                  },
                },
                {
                  label: t('tasks.zoom.quarter'),
                  onSelect: () => {
                    setZoom('quarter');
                  },
                },
              ],
            },
            {
              label: t('tasks.today'),
              icon: 'calendar-days' as const,
              onSelect: () => {
                setCentre((current) => current + 1);
              },
            },
          ]
        : []),
      /* ⚠️ **The create action moved to the HEADER** — `markup.html` 196–200's *Új feladat*.
             Import and export stay here: they are bulk operations over the list, not the primary
             thing this screen is for. */
    ],
    labels: listLabels(t),
  };

  /**
   * ⚠️ **The grid chooses its columns like the list does** (14 Oct 2026, ux-pass-2) — the same
   * toolbar control, the same hook, remembered apart from the list's because the two hold
   * different columns.
   */
  const gridChoice = useColumnChoice(
    GRID_COLUMNS.map((column) => ({ key: column.key, label: gridLabel(t, column.key) })),
    embedded === true ? 'project-task-grid' : 'task-grid',
  );
  const gridToolbar: ComponentProps<typeof ListToolbar> = {
    ...toolbar,
    columns: gridChoice.ordered.map((column) => ({ key: column.key, label: column.label })),
    hidden: gridChoice.hidden,
    onToggleColumn: gridChoice.toggle,
    onMoveColumn: gridChoice.move,
  };

  return (
    <div
      {...(embedded === true ? {} : { 'data-screen': mine ? 'my-tasks' : 'tasks' })}
      className="flex min-h-0 flex-1 flex-col"
    >
      {/* ⚠️ Not on the list view — its `DataList` draws this same set itself. */}
      {view === 'list' ? null : <ListToolbar {...(view === 'grid' ? gridToolbar : toolbar)} />}

      {serverError === null ? null : (
        <p className="text-destructive border-b px-5 py-2 text-[12px]" role="alert">
          {serverError}
        </p>
      )}

      {view === 'list' ? (
        <ListView
          columnsKey={embedded === true ? 'project-tasks' : 'tasks'}
          tasks={tasks}
          loading={loading}
          onOpen={onOpen}
          isFiltered={isFiltered}
          toolbar={toolbar}
          page={page}
          sort={sort}
          {...(onSortChange === undefined ? {} : { onSortChange })}
          statuses={statuses}
          priorities={priorities}
          members={members}
          {...(onBulkEdit === undefined ? {} : { onBulkEdit })}
          {...(onBulkDelete === undefined ? {} : { onBulkDelete })}
          metafields={metafields ?? []}
        />
      ) : loading && view === 'board' ? (
        <BoardSkeleton statuses={statuses} />
      ) : loading && view === 'timeline' ? (
        <TimelineSkeleton />
      ) : view === 'board' ? (
        <BoardView
          columnsKey={embedded === true ? 'project-tasks' : 'tasks'}
          metafields={metafields ?? []}
          tasks={tasks}
          statuses={statuses}
          canEdit={canEdit}
          onOpen={onOpen}
          onMoveStatus={onMoveStatus}
          {...(boardColumns === undefined ? {} : { pages: boardColumns })}
        />
      ) : view === 'timeline' ? (
        <TimelineView
          tasks={tasks}
          dependencies={dependencies ?? []}
          zoom={zoom}
          centre={centre}
          canEdit={canEdit}
          onOpen={onOpen}
          onReschedule={onReschedule}
          {...(page === undefined ? {} : { page })}
        />
      ) : (
        /* ⚠️ `page` was declared on `GridView` and never passed, so the pager it already knew how
           to draw was dead code and the grid silently showed one page of a longer list. */
        <GridView
          tasks={tasks}
          loading={loading}
          statuses={statuses}
          assignees={members}
          onEdit={onGridEdit}
          page={page}
          columnKeys={gridChoice.visible.map((column) => column.key)}
        />
      )}
    </div>
  );
}

/** One board column's server page: its filtered total, and its pager when it has more than one. */
export interface BoardColumnPage {
  readonly total: number;
  readonly page?: PagerState;
}

function zoomLabel(t: Translate, zoom: Zoom): string {
  if (zoom === 'week') return t('tasks.zoom.week');
  if (zoom === 'month') return t('tasks.zoom.month');
  return t('tasks.zoom.quarter');
}

/* ── Lista ─────────────────────────────────────────────────────────────────────────────────── */

/**
 * 20 Oct 2026 · LV-1 · the task card — the card grid's and the board's, which share one setting
 * (the product owner: *"use the same card setting"*).
 */
const TASK_CARD: CardSpec = {
  title: 'title',
  context: 'key',
  defaults: {
    badge: 'status',
    avatar: 'assignee',
    details: ['priority', 'due', 'estimate', 'project'],
  },
};

function ListView({
  columnsKey,
  tasks,
  loading,
  onOpen,
  isFiltered,
  toolbar,
  page,
  sort,
  onSortChange,
  statuses,
  priorities,
  members,
  onBulkEdit,
  onBulkDelete,
  metafields,
}: {
  /** Where the list remembers its columns — the project tab apart from `/tasks`. */
  readonly columnsKey: string;
  readonly tasks: readonly Task[];
  readonly loading: boolean;
  readonly onOpen: (task: Task) => void;
  /** ⚠️ Item 11 · whether a READER narrowed the list, which decides the empty state's sentence. */
  readonly isFiltered: boolean;
  /** ⚠️ Item 27 · the workspace's `task` fields. Those with `inList` become columns. */
  readonly metafields: readonly Metafield[];
  /** ⚠️ The vocabularies the bulk menu offers — the same three the row menu and the board use. */
  readonly statuses: readonly TaskStatus[];
  readonly priorities: readonly TaskPriority[];
  readonly members: readonly { id: string; name: string }[];
  readonly onBulkEdit?: (
    ids: readonly string[],
    patch: { statusId?: string; priorityId?: string; assigneeMemberId?: string | null },
  ) => void;
  readonly onBulkDelete?: (ids: readonly string[]) => void;
  /** ⚠️ The LIST's sort — a board and a timeline have no header to set one. See the screen's prop. */
  readonly sort: readonly SortState[];
  readonly onSortChange?: (next: readonly SortState[]) => void;
  /** The screen's one toolbar. ⚠️ Drawn HERE on this view — see the note where it is built. */
  readonly toolbar: ComponentProps<typeof ListToolbar>;
  /**
   * ⚠️ The server's page of the filtered set — the same one the grid and the timeline draw.
   */
  readonly page?: ComponentProps<typeof DataList>['page'];
}) {
  const t = useT();
  /* ⚠️ Asked for ONCE for the whole list — see `use-avatar.ts`: fifty rows assigned to six
   * people is six requests, not fifty. */
  const avatars = useAvatars(tasks.map((task) => task.assigneeAvatarUrl));

  /* ⚠️⚠️ **THE MAPPING IS `task-columns.ts`' AND NOT THIS SCREEN'S ANY MORE.** C27.2's agent renders
   * the tasks it read with the same rows and the same columns — CLAUDE.md's *"if a screen wants a
   * `TaskTable`, it wants the list component with task columns"* — and a second copy here would be
   * the invisible drift `docs/plan/agent.md` §4 names: the chat showing `8.0` where this shows
   * `8:00`. */
  const rows = taskRows(t, tasks, avatars, metafields);
  const columns = taskColumns(t, undefined, metafields);

  return (
    /* ⚠️ No padding: the mockup puts the table hard against both edges of the content area
       (`markup.html` 455), and a `p-5` here is the gap that was reported. */
    <div className="min-h-0 flex-1">
      <DataList
        {...toolbar}
        /* The columns somebody chose are remembered — the project tab separately from the global. */
        columnsKey={columnsKey}
        card={TASK_CARD}
        columns={columns}
        rows={rows}
        isLoading={loading}
        sort={sort}
        {...(onSortChange === undefined ? {} : { onSortChange })}
        /**
         * ┌─────────────────────────────────────────────────────────────────┐
         * │ ⚠️ ITEM 72 · the bulk bar. *Clear selection* was all it had.    │
         * └─────────────────────────────────────────────────────────────────┘
         *
         * ⚠️ **Absent, not disabled, without the permission** — `decisions.md` R-6, and the same
         * call the row menu makes one component over: an action somebody can never use is a
         * promise the server will refuse.
         *
         * ⚠️ **The status and priority menus offer the WORKSPACE's own vocabulary in ITS order**,
         * not an alphabetical list: `statuses` and `priorities` arrive ordered by `position`, which
         * is the order somebody arranged them in.
         */
        bulkActions={[
          ...(onBulkEdit === undefined
            ? []
            : [
                {
                  label: t('project.tasks.column.status'),
                  icon: 'circle-dot' as const,
                  menu: (ids: string[]) =>
                    statuses.map((status) => ({
                      label: status.name,
                      dot: status.color,
                      onSelect: () => {
                        onBulkEdit(ids, { statusId: status.id });
                      },
                    })),
                },
                {
                  label: t('project.tasks.column.priority'),
                  icon: 'flag' as const,
                  menu: (ids: string[]) =>
                    priorities.map((priority) => ({
                      label: priority.name,
                      dot: priority.color,
                      onSelect: () => {
                        onBulkEdit(ids, { priorityId: priority.id });
                      },
                    })),
                },
                {
                  label: t('project.tasks.column.assignee'),
                  icon: 'user-round' as const,
                  menu: (ids: string[]) => [
                    /* ⚠️ **Unassign is an ITEM, not the absence of one.** `null` clears the field;
                       leaving it out would make *"take these off Anna"* impossible from here. */
                    {
                      label: t('tasks.unassign'),
                      icon: 'user-round' as const,
                      onSelect: () => {
                        onBulkEdit(ids, { assigneeMemberId: null });
                      },
                    },
                    { type: 'separator' as const },
                    ...members.map((member) => ({
                      label: member.name,
                      onSelect: () => {
                        onBulkEdit(ids, { assigneeMemberId: member.id });
                      },
                    })),
                  ],
                },
              ]),
          ...(onBulkDelete === undefined
            ? []
            : [
                {
                  label: t('common.delete'),
                  icon: 'trash' as const,
                  destructive: true,
                  onSelect: (ids: string[]) => {
                    onBulkDelete(ids);
                  },
                },
              ]),
        ]}
        onRowClick={(id) => {
          const task = tasks.find((candidate) => candidate.id === id);
          if (task !== undefined) onOpen(task);
        }}
        empty={{
          icon: 'list-checks',
          /* ⚠️ Item 11 · the distinction lives HERE, where the screen knows whether a reader
             narrowed the list — and no longer on a board column, which never could. */
          title: isFiltered ? t('tasks.empty.filteredTitle') : t('tasks.empty.title'),
          body: isFiltered ? t('tasks.empty.filteredBody') : t('tasks.empty.body'),
        }}
        /**
         * ⚠️⚠️ **THE FOOTER COUNT IS GONE WHEN THE LIST PAGES, AND IT USED TO BE THE ONLY HONEST
         * FIGURE HERE.** It said *"…on this page"* precisely because a keyset could not know how
         * many there were; item 20's pager knows, and draws it. Two numbers three centimetres
         * apart — *"50 on this page"* beside *"84 results"* — is a reader working out which one
         * answers their question.
         */
        {...(page === undefined ? { footerNote: t('tasks.footer', { count: tasks.length }) } : {})}
        {...(page === undefined ? {} : { page })}
        labels={listLabels(t)}
      />
    </div>
  );
}

/* ── Kanban ────────────────────────────────────────────────────────────────────────────────── */

function BoardView({
  columnsKey,
  metafields,
  tasks,
  statuses,
  canEdit,
  onOpen,
  onMoveStatus,
  pages,
}: {
  /** 20 Oct 2026 · LV-1 · whose card layout the board's cards follow — the list's own. */
  readonly columnsKey: string;
  /** Each column's total and pager — the screen's `boardColumns`. */
  readonly pages?: Readonly<Record<string, BoardColumnPage>>;
  readonly metafields: readonly Metafield[];
  readonly tasks: readonly Task[];
  readonly statuses: readonly TaskStatus[];
  readonly canEdit: boolean;
  readonly onOpen: (task: Task) => void;
  readonly onMoveStatus: (task: Task, statusId: string) => void;
}) {
  const t = useT();
  const today = todayWire();
  const avatars = useAvatars(tasks.map((task) => task.assigneeAvatarUrl));

  /**
   * ⚠️⚠️ 20 Oct 2026 · LV-1 · **THE BOARD'S CARDS ARE THE LIST'S CARDS.** The same rows the
   * table draws, the same card the person set up for this list — so configuring the task card
   * once changes the grid and the board together. Outside the shell (no layout store) the board
   * keeps its own fixed card.
   */
  const store = useListLayoutStore();
  const cardRows = new Map(taskRows(t, tasks, avatars, metafields).map((row) => [row.id, row]));
  const cardColumns = taskColumns(t, undefined, metafields);
  const card = resolveCard(cardColumns, TASK_CARD, store?.layoutOf(columnsKey).card ?? null);
  const labelOf = (key: string): string =>
    cardColumns.find((column) => column.key === key)?.label ?? key;

  /* ⚠️ Columns come from `statuses`, in the workspace's own `position` order — not from the
   * statuses present among the loaded tasks. An empty column has to be visible, or there is
   * nowhere to drag a card to. */
  const columns: BoardColumn[] = statuses.map((status) => ({
    id: status.id,
    name: status.name,
    color: status.color,
    ...(pages === undefined ? {} : { total: pages[status.id]?.total ?? 0 }),
    cards: tasks
      .filter((task) => task.statusId === status.id)
      .map((task) => ({
        id: task.id,
        key: task.key,
        title: task.title,
        chipLabel: task.priorityName,
        chipColor: task.priorityColor,
        initials: task.assigneeName === null ? undefined : initialsOf(task.assigneeName),
        ...(task.assigneeAvatarSeed === null ? {} : { avatarSeed: task.assigneeAvatarSeed }),
        avatarSrc: avatars.get(task.assigneeAvatarUrl ?? ''),
        avatarColor: task.priorityColor,
        avatarTitle: task.assigneeName ?? undefined,
        dueLabel: task.dueOn ?? undefined,
        /* ⚠️ Late is computed here and passed as a boolean: the board does no date maths, which is
         * what keeps it domain-agnostic. */
        dueLate: task.dueOn !== null && task.dueOn < today && task.statusCategory !== 'closed',
        estimateLabel: task.estimateHours === '0.00' ? undefined : formatHours(task.estimateHours),
      })),
  }));

  return (
    <Board
      columns={columns}
      {...(store === null
        ? {}
        : {
            renderBody: (id: string) => {
              const row = cardRows.get(id);
              return row === undefined ? null : (
                <ListCardBody row={row} card={card} labelOf={labelOf} />
              );
            },
          })}
      renderFooter={(id) => {
        const page = pages?.[id]?.page;
        /* ⚠️ Only when the column has a second page — a pager under four cards is chrome. */
        return page === undefined || (page.count ?? 1) <= 1 ? null : (
          <div className="flex justify-center border-t px-1 py-1.5">
            <ListPager
              compact
              page={page}
              labels={{
                previousPage: t('list.previousPage'),
                nextPage: t('list.nextPage'),
                pageSelector: t('list.pageSelector'),
                pageOf: (current, count) => t('list.pageOf', { page: current, count }),
              }}
            />
          </div>
        );
      }}
      labels={{
        board: t('tasks.board'),
        column: (name) => t('tasks.board.column', { name }),
        move: t('tasks.board.move'),
        moveTo: (name) => t('tasks.board.moveTo', { name }),
        empty: t('tasks.board.empty'),
      }}
      onOpen={(id) => {
        const task = tasks.find((candidate) => candidate.id === id);
        if (task !== undefined) onOpen(task);
      }}
      onMove={
        canEdit
          ? (id, statusId) => {
              const task = tasks.find((candidate) => candidate.id === id);
              if (task !== undefined) onMoveStatus(task, statusId);
            }
          : undefined
      }
    />
  );
}

/**
 * The board before its first answer: the columns' frame with placeholder cards and no counts — an
 * empty column would say *drop a task here* to somebody whose tasks are still arriving.
 */
function BoardSkeleton({ statuses }: { readonly statuses: readonly TaskStatus[] }) {
  const t = useT();
  const columns =
    statuses.length === 0
      ? [0, 1, 2, 3].map((index) => ({ id: String(index), name: null, color: undefined }))
      : statuses.map((status) => ({ id: status.id, name: status.name, color: status.color }));

  return (
    <div className="min-h-0 flex-1 overflow-auto p-4">
      <div role="status" aria-live="polite" className="sr-only">
        {t('state.loading')}
      </div>
      <div aria-hidden className="flex h-full min-w-max gap-3">
        {columns.map((column, at) => (
          <section
            key={column.id}
            className="bg-muted/40 flex h-full w-[288px] shrink-0 flex-col rounded-xl border"
          >
            <header className="flex items-center gap-2 px-3 py-2.5">
              <span className="size-2 rounded-full" style={{ background: column.color }} />
              {column.name === null ? (
                <Skeleton width="96px" />
              ) : (
                <span className="text-[12.5px] font-medium">{column.name}</span>
              )}
            </header>
            <div className="flex flex-col gap-2 px-2 pb-2">
              {(at % 2 === 0 ? [0, 1, 2] : [0, 1]).map((index) => (
                <div
                  key={index}
                  className="bg-background flex flex-col gap-2 rounded-lg border p-3"
                >
                  <Skeleton width="30%" />
                  <Skeleton width={skeletonWidth(index + at, 1)} />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

/** The timeline before its first answer: the 60px date header and 30px rows, as placeholders. */
function TimelineSkeleton() {
  const t = useT();
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div role="status" aria-live="polite" className="sr-only">
        {t('state.loading')}
      </div>
      <div aria-hidden>
        <div className="bg-muted/40 h-[60px] border-b" />
        {SKELETON_WIDTHS.map((_widths, index) => (
          <div key={index} className="flex h-[30px] items-center gap-3 border-b px-3">
            <div className="w-[240px] shrink-0">
              <Skeleton width={skeletonWidth(index, 0)} />
            </div>
            <div className="flex-1">
              <Skeleton width={skeletonWidth(index, 2) / 2} className="h-4" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Idővonal ──────────────────────────────────────────────────────────────────────────────── */

function TimelineView({
  tasks,
  dependencies,
  zoom,
  centre,
  canEdit,
  onOpen,
  onReschedule,
  page,
}: {
  /** The server's page — the rows are one page of the scheduled tasks, like the list's. */
  readonly page?: PagerState;
  readonly tasks: readonly Task[];
  readonly dependencies: readonly TaskDependencyEdge[];
  readonly zoom: Zoom;
  readonly centre: number;
  readonly canEdit: boolean;
  readonly onOpen: (task: Task) => void;
  readonly onReschedule: (task: Task, startsOn: string, dueOn: string) => void;
}) {
  const t = useT();
  const today = todayWire();
  const avatars = useAvatars(tasks.map((task) => task.assigneeAvatarUrl));
  /* ⚠️ The app's one translated calendar vocabulary — `date-field.tsx`. The chart's period band
     names months and cannot reach `useT` from inside `@forecastify/ui`. */
  const calendar = useCalendarLabels();

  /**
   * ⚠️⚠️ **A TASK WITHOUT BOTH DATES HAS NO BAR, and the count of them is stated.** The mockup
   * filters to `t.start && t.due` and says nothing about the rest, so its timeline is quietly
   * shorter than its list — which reads as tasks having gone missing. Saying *"11 unscheduled"*
   * costs one line and turns a silent omission into information.
   */
  const scheduled = useMemo(
    () => tasks.filter((task) => task.startsOn !== null && task.dueOn !== null),
    [tasks],
  );
  const unscheduled = tasks.length - scheduled.length;

  const rows: GanttRow[] = scheduled.map((task) => ({
    key: task.key,
    title: task.title,
    start: task.startsOn ?? today,
    end: task.dueOn ?? today,
    color: task.projectColor ?? undefined,
    barColor: task.statusColor,
    /* ⚠️ Item 14's second frozen column. `undefined` rather than a blank name: the chart draws
       nobody as an em-dash with the word behind it, which a blank string could not say. */
    assignee:
      task.assigneeName === null
        ? undefined
        : {
            name: task.assigneeName,
            initials: initialsOf(task.assigneeName),
            ...(task.assigneeAvatarSeed === null ? {} : { avatarSeed: task.assigneeAvatarSeed }),
            avatarSrc: avatars.get(task.assigneeAvatarUrl ?? ''),
            color: task.priorityColor,
          },
    dependencies: arrowsFor(task, scheduled, dependencies),
  }));

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Gantt
        rows={rows}
        rangeFrom={rangeFrom(scheduled, today)}
        rangeTo={rangeTo(scheduled, today)}
        unitWidth={ZOOMS[zoom].unitWidth}
        unit={ZOOMS[zoom].unit}
        today={today}
        centerToken={centre}
        readOnly={!canEdit}
        labels={{
          timeline: t('tasks.timeline'),
          name: t('project.tasks.column.title'),
          assignee: t('project.tasks.column.assignee'),
          unassigned: t('report.group.unassigned'),
          /* ⚠️ Short month names, already translated — the band writes `szept 2026`. */
          months: calendar.months,
          bar: (row) => t('tasks.timeline.bar', { key: row.key, title: row.title }),
          today: t('tasks.today'),
          conflicts: (count) => t('tasks.timeline.conflicts', { count }),
          empty: t('tasks.timeline.empty'),
        }}
        onOpen={(key) => {
          const task = tasks.find((candidate) => candidate.key === key);
          if (task !== undefined) onOpen(task);
        }}
        onMove={
          canEdit
            ? (key, startsOn, dueOn) => {
                const task = tasks.find((candidate) => candidate.key === key);
                if (task !== undefined) onReschedule(task, startsOn, dueOn);
              }
            : undefined
        }
      />

      {unscheduled > 0 ? (
        <p className="text-muted-foreground border-t px-3 py-2 text-[12px]">
          {t('tasks.timeline.unscheduled', { count: unscheduled })}
        </p>
      ) : null}

      {page === undefined ? null : (
        /* ⚠️ The strip the list and the grid draw, so the three footers cannot drift apart. */
        <div className="bg-muted/30 text-muted-foreground flex shrink-0 flex-wrap items-center gap-2 border-t px-4 py-2 text-[11.5px]">
          <div className="flex-1" />
          <ListPager
            page={page}
            labels={{
              previousPage: t('list.previousPage'),
              nextPage: t('list.nextPage'),
              pageSelector: t('list.pageSelector'),
              pageOf: (current, count) => t('list.pageOf', { page: current, count }),
            }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ ONE YEAR BACK AND TWO FORWARD — AND THE NOTE HERE ARGUED THE OPPOSITE.│
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The note that stood here said the range should be derived from the data, a fortnight either side
 * of the actual work, because a fixed three years is mostly empty chart. It was answering a real
 * problem and it chose the wrong side of it — item 14, 14 Sep, asks for Jira's range in so many
 * words: *"one year back and two forward"*.
 *
 * ⚠️⚠️ **WHAT MADE THE DERIVED RANGE WRONG IS THAT THE CHART COULD NOT BE SCROLLED SOMEWHERE THAT
 * DOES NOT EXIST YET.** A plan is a thing you extend: dragging a bar past the last due date, or
 * looking at where next quarter would fall, both need canvas that no task occupies. A range that
 * ends a fortnight after the last task ends exactly where the planning starts.
 *
 * ⚠️ **The emptiness it worried about is answered by the other half of item 14** — the chart opens
 * centred on today (`centerToken`), the frozen columns list every row whether or not its bar is in
 * view, and the header now says which month you are looking at. None of those existed when the
 * fortnight was chosen.
 */
const RANGE_BACK_DAYS = 365;
const RANGE_FORWARD_DAYS = 730;

function rangeFrom(_tasks: readonly Task[], today: string): string {
  return shiftDays(today, -RANGE_BACK_DAYS);
}

function rangeTo(_tasks: readonly Task[], today: string): string {
  return shiftDays(today, RANGE_FORWARD_DAYS);
}

/* ── Rács ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * The mockup's `GRID_COLS`, its widths and its read-only flags, unchanged.
 *
 * ⚠️ **`k` and `proj` are read-only for real reasons, not for tidiness.** A task key never changes
 * (`decisions.md` R-17), and moving a task between projects is a membership question (P-1) that a
 * grid cell cannot ask — so both refuse the edit rather than accepting one the server would reject.
 */
const GRID_COLUMNS = [
  { key: 'key', width: 110, readOnly: true, mono: true },
  { key: 'title', width: 320, required: true },
  { key: 'project', width: 170, readOnly: true },
  { key: 'assignee', width: 150 },
  { key: 'status', width: 140 },
  { key: 'estimate', width: 100, mono: true },
  { key: 'startsOn', width: 120, mono: true },
  { key: 'dueOn', width: 120, mono: true },
] as const;

/**
 * ⚠️ **The grid does NOT take the status list, and an earlier draft did.** A grid cell edits text,
 * so a status change arrives here as a NAME — and resolving that name back to an id is the route's
 * job, because the route is what talks to the API. Passing the vocabularies in and then not using
 * them is how a component acquires props that exist to satisfy a reader.
 */
function GridView({
  tasks,
  loading,
  statuses,
  assignees,
  onEdit,
  page,
  columnKeys,
}: {
  readonly tasks: readonly Task[];
  readonly loading: boolean;
  /** The columns to draw, in order — the toolbar's chooser decides. */
  readonly columnKeys: readonly string[];
  /**
   * ⚠️⚠️ **THE GRID NOW TAKES THE VOCABULARIES, AND AN EARLIER NOTE ARGUED IT SHOULD NOT.** That
   * argument was about the WRITE — a cell edits text, so resolving a name back to an id is the
   * route's job — and it still holds: `onEdit` still hands up a string. What the grid needs them
   * for is the OFFER. A status cell that is a bare text field means knowing the workspace's status
   * names by heart, accents included, and learning about a typo from a refusal.
   */
  readonly statuses: readonly TaskStatus[];
  readonly assignees: readonly { id: string; name: string }[];
  readonly onEdit: (task: Task, column: string, value: string) => void;
  /** ⚠️ Item 32 · the grid pages like the list. Absent means it does not. */
  readonly page?: ComponentProps<typeof DataGrid>['page'];
}) {
  const t = useT();
  const [blocked, setBlocked] = useState<string | null>(null);

  const optionsFor = (key: string): readonly string[] | undefined => {
    if (key === 'status') return statuses.map((status) => status.name);
    /* ⚠️ An empty option first, so a cell can be CLEARED from the list rather than only by
     * selecting all and deleting — unassigning is an ordinary edit. */
    if (key === 'assignee') return ['', ...assignees.map((person) => person.name)];
    return undefined;
  };

  const columns: DataGridColumn[] = columnKeys
    .flatMap((key) => GRID_COLUMNS.filter((column) => column.key === key))
    .map((column) => ({
      key: column.key,
      label: gridLabel(t, column.key),
      width: column.width,
      mono: 'mono' in column ? column.mono : undefined,
      readOnly: 'readOnly' in column ? column.readOnly : undefined,
      required: 'required' in column ? column.required : undefined,
      options: optionsFor(column.key),
    }));

  const rows: DataGridRow[] = tasks.map((task) => ({
    id: task.id,
    key: task.key,
    title: task.title,
    project: task.projectName ?? t('projects.internal'),
    assignee: task.assigneeName ?? '',
    status: task.statusName,
    estimate: formatHours(task.estimateHours),
    startsOn: task.startsOn ?? '',
    dueOn: task.dueOn ?? '',
  }));

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DataGrid
        columns={columns}
        rows={rows}
        isLoading={loading}
        {...(page === undefined ? {} : { page })}
        labels={{
          grid: t('tasks.grid'),
          loading: t('state.loading'),
          readOnly: (label) => t('tasks.grid.readOnly', { label }),
          required: (label) => t('tasks.grid.required', { label }),
          /* ⚠️ The pager's words, from the same catalogue the list's come from. */
          previousPage: t('list.previousPage'),
          nextPage: t('list.nextPage'),
          pageSelector: t('list.pageSelector'),
          pageOf: (current, count) => t('list.pageOf', { page: current, count }),
          pageTotal: (total) => t('list.pageTotal', { total }),
        }}
        onChange={(rowId, key, value) => {
          const task = tasks.find((candidate) => candidate.id === rowId);
          if (task === undefined) return;

          /**
           * ⚠️ **The estimate is validated HERE as well as on the server**, because the grid's
           * whole point is a fast tab-through: a refusal that only arrives from the network lands
           * three cells later, next to the wrong row. `parseHours` is the same function the server
           * uses, so the two cannot disagree.
           */
          if (key === 'estimate' && value.trim() !== '' && parseHours(value) === null) {
            setBlocked(t('task.error.estimate'));
            return;
          }

          /**
           * ⚠️ **A value that is not one of the offered ones is refused HERE**, for the same reason
           * the estimate is: the grid's point is a fast tab-through, and a refusal that only
           * arrives from the network lands three cells later beside the wrong row. The combobox
           * makes a typo unlikely rather than impossible — it still accepts free text.
           */
          const allowed = optionsFor(key);
          if (allowed !== undefined && !allowed.includes(value.trim())) {
            setBlocked(t('tasks.grid.notAnOption', { value, column: gridLabel(t, key) }));
            return;
          }

          setBlocked(null);
          onEdit(task, key, value);
        }}
        onBlocked={setBlocked}
      />

      {/* ⚠️ A refusal is stated in words under the grid rather than as a red cell alone — the
          design system's rule, and the same shape the Gantt uses for its conflicts. */}
      {blocked === null ? null : (
        <p className="text-destructive border-t px-3 py-2 text-[12px]" role="alert">
          {blocked}
        </p>
      )}
    </div>
  );
}

/** One `t()` per branch with a literal key. */
function gridLabel(t: Translate, key: string): string {
  if (key === 'key') return t('project.tasks.column.key');
  if (key === 'title') return t('project.tasks.column.title');
  if (key === 'project') return t('task.field.project');
  if (key === 'assignee') return t('project.tasks.column.assignee');
  if (key === 'status') return t('project.tasks.column.status');
  if (key === 'estimate') return t('project.tasks.column.estimate');
  if (key === 'startsOn') return t('task.field.start');
  return t('project.tasks.column.due');
}

/* ── dates ─────────────────────────────────────────────────────────────────────────────────── */

/**
 * ⚠️ **Today from the LOCAL calendar, not `toISOString()`.** UTC names yesterday at 01:00 in
 * Budapest, so a task due today would render as late for every European evening.
 */
function todayWire(): string {
  const now = new Date();
  return `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
}

/** Local-parts arithmetic, so it is DST-safe — the same reason `dates.ts` avoids timestamps. */
function shiftDays(wire: string, days: number): string {
  const [year, month, day] = wire.split('-').map(Number);
  const date = new Date(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days);
  return `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * The arrows for one row: every edge whose SUCCESSOR is this task.
 *
 * ⚠️⚠️ **A DEPENDENCY WHOSE PREDECESSOR IS NOT SCHEDULED IS DROPPED HERE, NOT DRAWN AS NOTHING.**
 * `Gantt` looks the predecessor up among its own rows and returns null when it is absent, so an
 * unscheduled predecessor would silently produce no arrow either way — but filtering here is what
 * makes the *conflict count under the chart* honest: an edge that cannot be drawn must not be
 * counted as satisfied.
 *
 * ⚠️⚠️ **`violated` IS THE CLIENT'S ARITHMETIC, AND IT HAS TO BE.** It is a comparison of two dates
 * that both change while somebody drags a bar; a flag from the server would be stale before the
 * drag finished. The mockup computes it the same way, in `ganttRows`.
 *
 * ⚠️ **The rule is `successor.start < predecessor.end + lag`, and the boundary is deliberate.** A
 * successor starting on the day its predecessor is due is a same-day handoff, not a conflict — the
 * mockup's own `pt.due > t.start` draws exactly that line, and moving it one day would light up
 * every ordinary back-to-back plan in red.
 */
function arrowsFor(
  task: Task,
  scheduled: readonly Task[],
  edges: readonly TaskDependencyEdge[],
): GanttDependency[] {
  return edges
    .filter((edge) => edge.successorKey === task.key)
    .flatMap((edge) => {
      const predecessor = scheduled.find((candidate) => candidate.key === edge.predecessorKey);
      if (predecessor?.dueOn == null || task.startsOn === null) return [];

      const earliest = addWireDays(predecessor.dueOn, edge.lagDays) ?? predecessor.dueOn;
      return [
        {
          key: edge.predecessorKey,
          lagDays: edge.lagDays,
          violated: task.startsOn < earliest,
        },
      ];
    });
}
